import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Divider, Grid, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ContentCardsContainer, { ContentCard } from 'src/components/organisms/ContentCard';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

import type {
  ChartDataProvider,
  ChartRequest,
  DashboardBarKey,
  DashboardCreditNotesRow,
  DashboardDispatchedRow,
  DashboardPurchasingRow,
  DashboardReturnsRow,
  DashboardSalesRow,
  DashboardToShipRow,
  Period,
  SalesChannel,
  SalesRep,
} from './DashboardTypes';
import { DashboardGraphMobile } from './DashboardGraph';
import { daysInMonthForLabel, fmtInt, fmtMoney, getRollingDashboardLabelForDate, getRollingDashboardLabels, periodCopy } from './DashboardHelpers';
import { DASHBOARD_COLORS } from './DashboardColors';

type CardSizeProps = { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

type DashboardSalesMarginsRow = {
  SalesDate?: string | Date | null;
  OrderChannel?: string | null;
  ContributionMargin?: number | null;
  ContributionMarginRatio?: number | null;
};

export type DashboardCardMetrics = {
  range: { currentLabel: string; windowLabels: string[] };
  orders: { current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; previousWindowTotal?: number };
  orderValue: { current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; previousWindowTotal?: number };
  salesMargins: { hasData: boolean; current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; marginRatio: { online: number | null; store: number | null; total: number | null } };
  returns: { current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; previousWindowTotal?: number; rate: number | null; creditRate: number | null; trendPct: number | null };
  purchasing: { value: number };
  shipping: { shipped: number; toShip: number };
  creditNotes: { qty: number; value: number };
};

export type DashboardCardsProps = {
  chartPeriod: Period;
  selectedBars: DashboardBarKey[];
  selectedSalesChannels: SalesChannel[];
  viewBySalesRep: boolean;
  selectedSalesReps: string[];
  salesRepsData: SalesRep[];
  dataProvider: ChartDataProvider;
  dashboardSales?: DashboardSalesRow[];
  dashboardReturns?: DashboardReturnsRow[];
  dashboardPurchasing: DashboardPurchasingRow[];
  dashboardDispatched: DashboardDispatchedRow[];
  dashboardToShip: DashboardToShipRow[];
  dashboardCreditNotes: DashboardCreditNotesRow[];
  dashboardSalesMargins?: DashboardSalesMarginsRow[];
};

const sumByLabels = (labelsPick: string[], allLabels: string[], series: number[]) => {
  const indexByLabel = new Map<string, number>();
  allLabels.forEach((label, index) => indexByLabel.set(label, index));
  return labelsPick.reduce((acc, label) => acc + (series[indexByLabel.get(label) ?? -1] ?? 0), 0);
};

const pack = (online: number, store: number) => ({ online, store, total: online + store });
const pctChange = (current: number, previous: number) => {
  if (!Number.isFinite(previous) || previous < 0) return null;
  if (previous === 0) return current === 0 ? 0 : 100;
  return ((current - previous) / previous) * 100;
};
const fmtPct = (value: number | null, digits = 1) => (value == null || !Number.isFinite(value) ? '—' : `${value.toFixed(digits)}%`);
const ratioFromParts = (margin: number, revenue: number) => revenue > 0 ? (margin / revenue) * 100 : null;

export const inferSalesChannelFromOrderChannel = (orderChannel?: string | null): 'Store' | 'Online' | null => {
  const normalized = String(orderChannel ?? '').trim().toLowerCase();
  if (normalized === 'web' || normalized === 'online' || normalized === 'direct' ) return 'Online';
  if (normalized === 'store') return 'Store';
  return null;
};

export const buildDashboardCardMetrics = (args: {
  dataProvider: ChartDataProvider;
  period: Period;
  dashboardSales?: DashboardSalesRow[];
  dashboardReturns?: DashboardReturnsRow[];
  dashboardPurchasing?: DashboardPurchasingRow[];
  dashboardDispatched?: DashboardDispatchedRow[];
  dashboardToShip?: DashboardToShipRow[];
  dashboardCreditNotes?: DashboardCreditNotesRow[];
  dashboardSalesMargins?: DashboardSalesMarginsRow[];
}): DashboardCardMetrics => {
  const {
    dataProvider,
    period,
    dashboardPurchasing = [],
    dashboardDispatched = [],
    dashboardToShip = [],
    dashboardCreditNotes = [],
    dashboardSalesMargins = [],
  } = args;
  

  const salesRows = ((args as any).dashboardSales ?? []) as DashboardSalesRow[];
  const returnsRows = ((args as any).dashboardReturns ?? []) as DashboardReturnsRow[];

  const labels = getRollingDashboardLabels(period);
  const currentLabel = labels[labels.length - 1] ?? '';
  const windowLabels = labels;

  const previousWindowRange = (() => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);

    if (period === 'Week') {
      end.setDate(end.getDate() - 7);
      start.setTime(end.getTime());
      start.setDate(start.getDate() - 6);
      return { start, end };
    }

    if (period === 'Month') {
      end.setDate(end.getDate() - 30);
      start.setTime(end.getTime());
      start.setDate(start.getDate() - 29);
      return { start, end };
    }

    return null;
  })();

  const req: ChartRequest = {
    period,
    selectedBars: ['ordersBar', 'ordersValueBar', 'returnsBar', 'toShipBar', 'shippedBar', 'purchasesValueBar', 'creditNotesBar', 'creditNotesValueBar'],
    selectedChannels: ['Online', 'Store'],
    byRep: false,
    selectedReps: [],
    allReps: [],
    orientation: 'vertical',
  };

  const buckets = dataProvider.getStandardBuckets(req, labels);

  const salesMarginsByLabel = new Map<string, { online: number; store: number; onlineRevenue: number; storeRevenue: number }>();
  labels.forEach(label => salesMarginsByLabel.set(label, { online: 0, store: 0, onlineRevenue: 0, storeRevenue: 0 }));

  dashboardSalesMargins.forEach(row => {
    const label = getRollingDashboardLabelForDate(row.SalesDate, period, labels);
    if (!label) return;

    const channel = inferSalesChannelFromOrderChannel(row.OrderChannel);
    if (!channel) return;

    const contributionMargin = Number(row.ContributionMargin ?? 0) || 0;
    const contributionMarginRatio = Number(row.ContributionMarginRatio ?? 0) || 0;
    const impliedRevenue = contributionMarginRatio !== 0 && Number.isFinite(contributionMarginRatio) ? contributionMargin / contributionMarginRatio : 0;

    const bucket = salesMarginsByLabel.get(label)!;

    if (channel === 'Online') {
      bucket.online += contributionMargin;
      bucket.onlineRevenue += impliedRevenue;
    } else {
      bucket.store += contributionMargin;
      bucket.storeRevenue += impliedRevenue;
    }
  });

  const salesMarginsOnlineSeries = labels.map(label => salesMarginsByLabel.get(label)?.online ?? 0);
  const salesMarginsStoreSeries = labels.map(label => salesMarginsByLabel.get(label)?.store ?? 0);
  const salesMarginRevenueOnlineSeries = labels.map(label => salesMarginsByLabel.get(label)?.onlineRevenue ?? 0);
  const salesMarginRevenueStoreSeries = labels.map(label => salesMarginsByLabel.get(label)?.storeRevenue ?? 0);

  const ordersCurrentOnline = sumByLabels([currentLabel], labels, buckets.ordersOnline);
  const ordersCurrentStore = sumByLabels([currentLabel], labels, buckets.ordersStore);
  const valueCurrentOnline = sumByLabels([currentLabel], labels, buckets.ordersValueOnline);
  const valueCurrentStore = sumByLabels([currentLabel], labels, buckets.ordersValueStore);
  const returnsCurrentOnline = sumByLabels([currentLabel], labels, buckets.returnsOnline);
  const returnsCurrentStore = sumByLabels([currentLabel], labels, buckets.returnsStore);
  const salesMarginsCurrentOnline = sumByLabels([currentLabel], labels, salesMarginsOnlineSeries);
  const salesMarginsCurrentStore = sumByLabels([currentLabel], labels, salesMarginsStoreSeries);

  const ordersWindowOnline = sumByLabels(windowLabels, labels, buckets.ordersOnline);
  const ordersWindowStore = sumByLabels(windowLabels, labels, buckets.ordersStore);
  const valueWindowOnline = sumByLabels(windowLabels, labels, buckets.ordersValueOnline);
  const valueWindowStore = sumByLabels(windowLabels, labels, buckets.ordersValueStore);
  const returnsWindowOnline = sumByLabels(windowLabels, labels, buckets.returnsOnline);
  const returnsWindowStore = sumByLabels(windowLabels, labels, buckets.returnsStore);
  const salesMarginsWindowOnline = sumByLabels(windowLabels, labels, salesMarginsOnlineSeries);
  const salesMarginsWindowStore = sumByLabels(windowLabels, labels, salesMarginsStoreSeries);
  const salesMarginRevenueWindowOnline = sumByLabels(windowLabels, labels, salesMarginRevenueOnlineSeries);
  const salesMarginRevenueWindowStore = sumByLabels(windowLabels, labels, salesMarginRevenueStoreSeries);

  const inPreviousWindow = (value?: string | Date | null) => {
    if (!previousWindowRange || !value) return false;
    const dt = new Date(value);
    if (!Number.isFinite(+dt)) return false;
    dt.setHours(0, 0, 0, 0);
    return dt >= previousWindowRange.start && dt <= previousWindowRange.end;
  };

  const hasPreviousSalesData = previousWindowRange ? salesRows.some(row => inPreviousWindow(row?.SalesDate)) : false;
  const hasPreviousReturnsData = previousWindowRange ? returnsRows.some(row => inPreviousWindow(row?.ReturnCreateDate)) : false;

  const previousOrdersTotal = previousWindowRange
    ? salesRows.reduce((sum, row) => sum + (inPreviousWindow(row?.SalesDate) ? (Number(row?.Count ?? 0) || 0) : 0), 0)
    : 0;
  const previousOrderValueTotal = previousWindowRange
    ? salesRows.reduce((sum, row) => sum + (inPreviousWindow(row?.SalesDate) ? (Number(row?.TotalAmountNet ?? 0) || 0) : 0), 0)
    : 0;
  const previousReturnsTotal = previousWindowRange
    ? returnsRows.reduce((sum, row) => sum + (inPreviousWindow(row?.ReturnCreateDate) ? (Number(row?.Count ?? 0) || 0) : 0), 0)
    : 0;

  const purchasingValue = sumByLabels(windowLabels, labels, buckets.purchasesValueOnline);
  const shipped = sumByLabels(windowLabels, labels, buckets.shippedOnline) + sumByLabels(windowLabels, labels, buckets.shippedStore);
  const toShip = sumByLabels(windowLabels, labels, buckets.toShipOnline) + sumByLabels(windowLabels, labels, buckets.toShipStore);
  const creditQty = sumByLabels(windowLabels, labels, buckets.creditNotesOnline);
  const creditValue = sumByLabels(windowLabels, labels, buckets.creditNotesValueOnline);

  let divisorDays = 1;
  if (period === 'Week') divisorDays = 7;
  else if (period === 'Month') divisorDays = 30;
  else divisorDays = Math.max(1, windowLabels.map(label => daysInMonthForLabel(label)).reduce((a, b) => a + b, 0));

  return {
    range: { currentLabel, windowLabels },
    orders: {
      current: pack(ordersCurrentOnline, ordersCurrentStore),
      window: pack(ordersWindowOnline, ordersWindowStore),
      avgPerDay: pack(ordersWindowOnline / divisorDays, ordersWindowStore / divisorDays),
      previousWindowTotal: previousWindowRange && hasPreviousSalesData ? previousOrdersTotal : null,
    },
    orderValue: {
      current: pack(valueCurrentOnline, valueCurrentStore),
      window: pack(valueWindowOnline, valueWindowStore),
      avgPerDay: pack(valueWindowOnline / divisorDays, valueWindowStore / divisorDays),
      previousWindowTotal: previousWindowRange && hasPreviousSalesData ? previousOrderValueTotal : null,
    },
    salesMargins: {
      hasData: dashboardSalesMargins.length > 0,
      current: pack(salesMarginsCurrentOnline, salesMarginsCurrentStore),
      window: pack(salesMarginsWindowOnline, salesMarginsWindowStore),
      avgPerDay: pack(salesMarginsWindowOnline / divisorDays, salesMarginsWindowStore / divisorDays),
      marginRatio: {
        online: ratioFromParts(salesMarginsWindowOnline, salesMarginRevenueWindowOnline),
        store: ratioFromParts(salesMarginsWindowStore, salesMarginRevenueWindowStore),
        total: ratioFromParts(salesMarginsWindowOnline + salesMarginsWindowStore, salesMarginRevenueWindowOnline + salesMarginRevenueWindowStore),
      },
    },
    returns: {
      current: pack(returnsCurrentOnline, returnsCurrentStore),
      window: pack(returnsWindowOnline, returnsWindowStore),
      avgPerDay: pack(returnsWindowOnline / divisorDays, returnsWindowStore / divisorDays),
      previousWindowTotal: previousWindowRange && hasPreviousReturnsData ? previousReturnsTotal : null,
      rate: (ordersWindowOnline + ordersWindowStore) > 0 ? ((returnsWindowOnline + returnsWindowStore) / (ordersWindowOnline + ordersWindowStore)) * 100 : null,
      creditRate: (valueWindowOnline + valueWindowStore) > 0 ? (creditValue / (valueWindowOnline + valueWindowStore)) * 100 : null,
      trendPct: previousWindowRange && hasPreviousReturnsData ? pctChange(returnsWindowOnline + returnsWindowStore, previousReturnsTotal) : null,
    },
    purchasing: { value: purchasingValue },
    shipping: { shipped, toShip },
    creditNotes: { qty: creditQty, value: creditValue },
  };
};

export const useDashboardCardMetrics = (args: {
  dataProvider: ChartDataProvider;
  period: Period;
  dashboardSales?: DashboardSalesRow[];
  dashboardReturns?: DashboardReturnsRow[];
  dashboardPurchasing?: DashboardPurchasingRow[];
  dashboardDispatched?: DashboardDispatchedRow[];
  dashboardToShip?: DashboardToShipRow[];
  dashboardCreditNotes?: DashboardCreditNotesRow[];
  dashboardSalesMargins?: DashboardSalesMarginsRow[];
}) => useMemo(() => buildDashboardCardMetrics(args), [args]);

export const DashboardHeadlineRow: React.FC<{
  title: string;
  tooltip: string;
  icon: React.ReactNode;
  iconColor: string;
  mainValue: string | number |React.ReactNode;
  currentValue: string | number;
  avg: string | number;
  currentLabel?: string;
  trendLabel?: string | null;
  trendColor?: string;
  extraFooter?: React.ReactNode;
}> = ({ title, tooltip, icon, iconColor, mainValue, currentValue, avg, currentLabel = 'Today', trendLabel, trendColor, extraFooter }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1, minWidth: 0 }}>
      <Tooltip title={tooltip} arrow placement="top">
        <Avatar sx={{ bgcolor: alpha(iconColor, 0.12), color: iconColor, mr: 2, width: 48, height: 48, flexShrink: 0 }}>
          {icon}
        </Avatar>
      </Tooltip>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{title}</Typography>
          {trendLabel ? <Typography variant="caption" sx={{ color: trendColor ?? 'text.secondary', fontWeight: 700, lineHeight: 1, transform: 'translateY(-1px)' }}>{trendLabel}</Typography> : null}
        </Box>
        <Box sx={{ mt: 0.25, mb: 0.5, minWidth: 0 }}>
  {typeof mainValue === 'string' || typeof mainValue === 'number' ? (
    <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1, color: 'text.primary', wordWrap: 'break-word' }}>
      {mainValue}
    </Typography>
  ) : mainValue}
</Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">{currentLabel}:{' '}<Typography component="span" variant="caption" fontWeight={600} color="text.primary">{currentValue}</Typography></Typography>
          <Typography variant="caption" color="text.secondary">{t('Avg/day')}:{' '}<Typography component="span" variant="caption" fontWeight={600} color="text.primary">{avg}</Typography></Typography>
        </Box>
        {extraFooter ? <Box sx={{ mt: 1.1 }}>{extraFooter}</Box> : null}
      </Box>
    </Box>
  );
};

export const DashboardDualMetricRow: React.FC<{
  tooltip: string;
  icon: React.ReactNode;
  iconColor: string;
  leftTitle: string;
  leftValue: string | number;
  leftTooltip?: string;
  rightTitle: string;
  rightValue: string | number;
  rightTooltip?: string;
}> = ({ tooltip, icon, iconColor, leftTitle, leftValue, leftTooltip, rightTitle, rightValue, rightTooltip }) => {
  const MetricBlock = ({ title, value, blockTooltip }: { title: string; value: string | number; blockTooltip?: string }) => {
    const content = (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2, mt: 0.35 }}>
          {value}
        </Typography>
      </Box>
    );

    return blockTooltip ? (
      <Tooltip title={blockTooltip} arrow placement="top">
        {content}
      </Tooltip>
    ) : content;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1, minWidth: 0 }}>
      <Tooltip title={tooltip} arrow placement="top">
        <Avatar sx={{ bgcolor: alpha(iconColor, 0.12), color: iconColor, mr: 2, width: 48, height: 48, flexShrink: 0 }}>
          {icon}
        </Avatar>
      </Tooltip>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <MetricBlock title={leftTitle} value={leftValue} blockTooltip={leftTooltip} />
        <MetricBlock title={rightTitle} value={rightValue} blockTooltip={rightTooltip} />
      </Box>
    </Box>
  );
};

export const DashboardChannelDonut: React.FC<{
  online: number;
  store: number;
  onlineColor: string;
  storeColor: string;
  size?: number;
}> = ({ online, store, onlineColor, storeColor, size = 64 }) => {
  const total = online + store;
  const pct = total === 0 ? 0 : online / total;
  const r = 15.91549430918954;
  const c = 100;
  const dash = pct * c;
  const emptyColor = alpha('#808080', 0.2);

  return (
    <svg width={size} height={size} viewBox="0 0 42 42" style={{ transform: 'rotate(-90deg)', overflow: 'visible', flexShrink: 0 }}>
      <circle cx="21" cy="21" r={r} fill="transparent" stroke={total === 0 ? emptyColor : storeColor} strokeWidth="6" />
      {total > 0 && online > 0 && <circle cx="21" cy="21" r={r} fill="transparent" stroke={onlineColor} strokeWidth="6" strokeDasharray={`${dash} ${c}`} />}
    </svg>
  );
};

export const DashboardChannelBreakdownCard: React.FC<{ metrics: DashboardCardMetrics; period: Period; sizes?: CardSizeProps; minHeight?: number; }> = ({ metrics, period, sizes, minHeight }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const copy = periodCopy(period);
  const revenueColors = DASHBOARD_COLORS.orderValue;
  const orderColors = DASHBOARD_COLORS.orders;
  const salesMarginColors = DASHBOARD_COLORS.salesMargins;
  const md = sizes?.md ?? 12;

  const ChannelSplit = ({ label, icon, rawTotal, rawOnline, rawStore, formatter, onlineColor, storeColor, footer }: any) => {
    const safeTotal = rawTotal === 0 ? 1 : rawTotal;
    const pctOnline = Math.round((rawOnline / safeTotal) * 100);
    const pctStore = Math.round((rawStore / safeTotal) * 100);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2.5 }}>
        <DashboardChannelDonut online={rawOnline} store={rawStore} onlineColor={onlineColor} storeColor={storeColor} size={68} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mb: 0.25 }}>{icon}<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t(label)}</Typography></Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0.5 }}>{formatter(rawTotal)}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: onlineColor, fontWeight: 700 }}>{pctOnline}% <Typography component="span" variant="caption" fontWeight={500} color="text.secondary">({formatter(rawOnline)})</Typography></Typography>
            <Typography variant="caption" sx={{ color: storeColor, fontWeight: 700 }}>{pctStore}% <Typography component="span" variant="caption" fontWeight={500} color="text.secondary">({formatter(rawStore)})</Typography></Typography>
          </Box>
          {footer ? <Box sx={{ mt: 0.5 }}>{footer}</Box> : null}
        </Box>
      </Box>
    );
  };

  return (
    <ContentCard xs={sizes?.xs ?? 12} sm={sizes?.sm} md={md} lg={sizes?.lg} xl={sizes?.xl} cardId="dashboard-channel-card" contentPadding={0} cardHeader={t('Channel Breakdown ({{windowHeader}})', { windowHeader: copy.windowHeader })} cardTypes={[]}>
      <Box sx={{ p: 2, pt: 3, minHeight }}>
        <Stack direction="row" spacing={3} sx={{ mb: 3, justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: orderColors.online, mr: 1 }} /><Typography variant="body2" fontWeight={600} color="text.secondary">{t('Online')}</Typography></Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: orderColors.store, mr: 1 }} /><Typography variant="body2" fontWeight={600} color="text.secondary">{t('In-Store')}</Typography></Box>
        </Stack>
        <ChannelSplit label={t('Revenue Share')} icon={<PaidRoundedIcon fontSize="small" />} rawTotal={metrics.orderValue.window.total} rawOnline={metrics.orderValue.window.online} rawStore={metrics.orderValue.window.store} formatter={fmtMoney} onlineColor={revenueColors.online} storeColor={revenueColors.store} />
        {metrics.salesMargins.hasData && (
          <ChannelSplit
            label={t('Margin Share')}
            icon={<PercentRoundedIcon fontSize="small" />}
            rawTotal={metrics.salesMargins.window.total}
            rawOnline={metrics.salesMargins.window.online}
            rawStore={metrics.salesMargins.window.store}
            formatter={fmtMoney}
            onlineColor={salesMarginColors.online}
            storeColor={salesMarginColors.store}
            footer={
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">{t('Online margin')}:{' '}<Typography component="span" variant="caption" fontWeight={600} color="text.primary">{fmtPct(metrics.salesMargins.marginRatio.online)}</Typography></Typography>
                <Typography variant="caption" color="text.secondary">{t('Store margin')}:{' '}<Typography component="span" variant="caption" fontWeight={600} color="text.primary">{fmtPct(metrics.salesMargins.marginRatio.store)}</Typography></Typography>
              </Box>
            }
          />
        )}
        <ChannelSplit label={t('Orders Share (Qty)')} icon={<LocalMallRoundedIcon fontSize="small" />} rawTotal={metrics.orders.window.total} rawOnline={metrics.orders.window.online} rawStore={metrics.orders.window.store} formatter={fmtInt} onlineColor={orderColors.online} storeColor={orderColors.store} />
      </Box>
    </ContentCard>
  );
};

export const DashboardExecutiveSummaryCard: React.FC<{ metrics: DashboardCardMetrics; period: Period; sizes?: CardSizeProps; minHeight?: number; }> = ({ metrics, period, sizes, minHeight }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const copy = periodCopy(period);
  const md = sizes?.md ?? 12;
  const getTrendPresentation = (value: number | null | undefined) => {
    if (period === 'Year' || value == null || !Number.isFinite(value)) return { label: null, color: undefined as string | undefined };
    return {
      label: `${value >= 0 ? '+' : ''}${value.toFixed(0)}%`,
      color: DASHBOARD_COLORS.trend,
    };
  };

  const revenueTrend = getTrendPresentation(metrics.orderValue.previousWindowTotal != null ? pctChange(metrics.orderValue.window.total, metrics.orderValue.previousWindowTotal) : null);
  const ordersTrend = getTrendPresentation(metrics.orders.previousWindowTotal != null ? pctChange(metrics.orders.window.total, metrics.orders.previousWindowTotal) : null);
  const returnsTrend = getTrendPresentation(metrics.returns.trendPct);

  return (
    <ContentCard xs={sizes?.xs ?? 12} sm={sizes?.sm} md={md} lg={sizes?.lg} xl={sizes?.xl} cardId="dashboard-summary-card" contentPadding={0} cardHeader={t('Performance ({{windowHeader}})', { windowHeader: copy.windowHeader })} cardTypes={[]}>
      <Box sx={{ p: 2, minHeight }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))', xl: '1fr' },
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ minWidth: 0, px: { xs: 0, md: 1, xl: 0 } }}>
            <DashboardHeadlineRow title={t('Total Revenue')} tooltip={t('Total revenue is all money coming in from orders, including products, tax, shipping and other charges before returns or credits are subtracted.')} icon={<PaidRoundedIcon fontSize="medium" />} iconColor={DASHBOARD_COLORS.orderValue.store} mainValue={fmtMoney(metrics.orderValue.window.total)} currentValue={fmtMoney(metrics.orderValue.current.total)} avg={fmtMoney(metrics.orderValue.avgPerDay.total)} currentLabel={copy.currentLabel} trendLabel={revenueTrend.label} trendColor={revenueTrend.color} />
          </Box>

          {metrics.salesMargins.hasData && (
            <>
              <Divider sx={{ mx: 2, my: 0.5, opacity: 0.4, display: { xs: 'block', md: 'none', xl: 'block' } }} />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, opacity: 0.4, display: { xs: 'none', md: 'block', xl: 'none' } }} />

              <Box sx={{ minWidth: 0, px: { xs: 0, md: 1, xl: 0 } }}>
              <DashboardHeadlineRow
  title={t('Contribution Margin')}
  tooltip={t('Contribution margin is the money gained from the product markup after product cost. It does not include other costs like ads, salaries, rent or tools that can reduce the final profit.')}
  icon={<PercentRoundedIcon fontSize="medium" />}
  iconColor={DASHBOARD_COLORS.salesMargins.store}
  mainValue={
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
      <Typography component="span" variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1, color: 'text.primary' }}>
        {fmtMoney(metrics.salesMargins.window.total)}
      </Typography>
      <Tooltip title={t('This percentage is the total average markup for the selected period. It compares total contribution margin with the revenue behind those sales.')} arrow placement="top">
        <Typography component="span" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, color: DASHBOARD_COLORS.salesMargins.store, cursor: 'help' }}>
          {fmtPct(metrics.salesMargins.marginRatio.total)}
        </Typography>
      </Tooltip>
    </Box>
  }
  currentValue={fmtMoney(metrics.salesMargins.current.total)}
  avg={fmtMoney(metrics.salesMargins.avgPerDay.total)}
  currentLabel={copy.currentLabel}
/>
              </Box>
            </>
          )}

          <Divider sx={{ mx: 2, my: 0.5, opacity: 0.4, display: { xs: 'block', md: 'none', xl: 'block' } }} />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, opacity: 0.4, display: { xs: 'none', md: 'block', xl: 'none' } }} />

          <Box sx={{ minWidth: 0, px: { xs: 0, md: 1, xl: 0 } }}>
            <DashboardHeadlineRow title={t('Total Orders')} tooltip={t('Total number of orders for the selected period window')} icon={<LocalMallRoundedIcon fontSize="medium" />} iconColor={DASHBOARD_COLORS.orders.store} mainValue={fmtInt(metrics.orders.window.total)} currentValue={fmtInt(metrics.orders.current.total)} avg={fmtInt(metrics.orders.avgPerDay.total)} currentLabel={copy.currentLabel} trendLabel={ordersTrend.label} trendColor={ordersTrend.color} />
          </Box>

          <Divider sx={{ mx: 2, my: 0.5, opacity: 0.4, display: { xs: 'block', md: 'none', xl: 'block' } }} />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, opacity: 0.4, display: { xs: 'none', md: 'block', xl: 'none' } }} />

          <Box sx={{ minWidth: 0, px: { xs: 0, md: 1, xl: 0 } }}>
            <DashboardHeadlineRow
              title={t('Returns')}
              tooltip={t('Returns are the number of orders customers sent back during this period.')}
              icon={<KeyboardReturnRoundedIcon fontSize="medium" />}
              iconColor={DASHBOARD_COLORS.returns}
              mainValue={fmtInt(metrics.returns.window.total)}
              currentValue={fmtInt(metrics.returns.current.total)}
              avg={fmtInt(metrics.returns.avgPerDay.total)}
              currentLabel={copy.currentLabel}
              trendLabel={returnsTrend.label}
              trendColor={returnsTrend.color}
            />
          </Box>

          <Divider sx={{ mx: 2, my: 0.5, opacity: 0.4, display: { xs: 'block', md: 'none', xl: 'block' } }} />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, opacity: 0.4, display: { xs: 'none', md: 'block', xl: 'none' } }} />

          <Box sx={{ minWidth: 0, px: { xs: 0, md: 1, xl: 0 } }}>
            <DashboardDualMetricRow
              tooltip={t('Return rate and credit rate show how much returns affect orders and revenue.')}
              icon={<PercentRoundedIcon fontSize="medium" />}
              iconColor={DASHBOARD_COLORS.credits}
              leftTitle={t('Return rate')}
              leftValue={fmtPct(metrics.returns.rate)}
              leftTooltip={t('Return rate is the number of returns as a percentage of the total number of orders.')}
              rightTitle={t('Credit rate')}
              rightValue={fmtPct(metrics.returns.creditRate)}
              rightTooltip={t('Credit rate is the money paid back through credits and returns compared with total revenue.')}
            />
          </Box>
        </Box>
      </Box>
    </ContentCard>
  );
};

export const DashboardOperationsOverviewCard: React.FC<{ metrics: DashboardCardMetrics; period: Period; sizes?: CardSizeProps; minHeight?: number; }> = ({ metrics, period, sizes, minHeight }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const copy = periodCopy(period);
  const md = sizes?.md ?? 12;

  return (
    <ContentCard xs={sizes?.xs ?? 12} sm={sizes?.sm} md={md} lg={sizes?.lg} xl={sizes?.xl} cardId="dashboard-operations-card" contentPadding={0} cardHeader={t('Operations Overview ({{windowHeader}})', { windowHeader: copy.windowHeader })} cardTypes={[]}>
      <Box sx={{ p: 2, minHeight }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Tooltip title={t('Orders waiting to be shipped during the selected dashboard period')}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(DASHBOARD_COLORS.toShip, 0.07), border: 1, borderColor: alpha(DASHBOARD_COLORS.toShip, 0.8), height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}><LocalShippingRoundedIcon fontSize="small" sx={{ color: DASHBOARD_COLORS.toShip }} /><Typography variant="caption" fontWeight={600} color="text.secondary">{t('TO SHIP')}</Typography></Box>
                <Typography variant="h4" color="text.primary" fontWeight={700}>{fmtInt(metrics.shipping.toShip)}</Typography>
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6}>
            <Tooltip title={t('Orders shipped during the selected dashboard period')}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default', border: 1, borderColor: 'divider', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}><LocalShippingRoundedIcon fontSize="small" sx={{ color: DASHBOARD_COLORS.shipped }} /><Typography variant="caption" fontWeight={600} color="text.secondary">{t('SHIPPED')}</Typography></Box>
                <Typography variant="h4" color="text.primary" fontWeight={700}>{fmtInt(metrics.shipping.shipped)}</Typography>
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6}>
            <Tooltip title={t('Credit note count during the selected dashboard period')}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: DASHBOARD_COLORS.operationsCardBg.creditQty, border: 1, borderColor: alpha("#fff", 0.3), height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}><ReceiptLongRoundedIcon fontSize="small" sx={{ color: DASHBOARD_COLORS.credits }} /><Typography variant="caption" fontWeight={600} color="text.secondary">{t('CREDITS')}</Typography></Box>
                <Typography variant="h4" color="text.primary" fontWeight={700}>{fmtInt(metrics.creditNotes.qty)}</Typography>
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={6}>
            <Tooltip title={t('Total credit note net value during the selected dashboard period')}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: DASHBOARD_COLORS.operationsCardBg.creditValue, border: 1, borderColor: alpha("#fff", 0.3), height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}><ReceiptLongRoundedIcon sx={{ color: DASHBOARD_COLORS.creditsValue }} /><Typography variant="caption" fontWeight={600} color="text.secondary">{t('CREDITS VALUE')}</Typography></Box>
                <Typography variant="h4" color="text.primary" fontWeight={700}>{fmtMoney(metrics.creditNotes.value)}</Typography>
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Tooltip title={t('Total purchasing value during the selected dashboard period')}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default', border: 1, borderColor: 'divider', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}><ShoppingCartRoundedIcon sx={{ color: DASHBOARD_COLORS.purchasing }} /><Typography variant="caption" fontWeight={600} color="text.secondary">{t('PURCHASING')}</Typography></Box>
                <Typography variant="h4" color="text.primary" fontWeight={700}>{fmtMoney(metrics.purchasing.value)}</Typography>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
    </ContentCard>
  );
};

export const DashboardHistoricalChartCard: React.FC<{ chartPeriod: Period; selectedBars: DashboardBarKey[]; selectedSalesChannels: SalesChannel[]; viewBySalesRep: boolean; selectedSalesReps: string[]; salesRepsData: SalesRep[]; dataProvider: ChartDataProvider; sizes?: CardSizeProps; }> = (props) => {
  const { t } = useTranslation();
  const md = props.sizes?.md ?? 12;
  return (
    <ContentCard xs={props.sizes?.xs ?? 12} sm={props.sizes?.sm} md={md} lg={props.sizes?.lg} xl={props.sizes?.xl} contentPadding={0} cardId="dashboard-historical-chart-card" cardHeader={t('Historical Overview ({{period}})', { period: t(props.chartPeriod) })} cardTypes={[]}>
      <DashboardGraphMobile
        period={props.chartPeriod}
        selectedBars={props.selectedBars}
        selectedSalesChannels={props.selectedSalesChannels}
        viewBySalesRep={props.viewBySalesRep}
        selectedSalesReps={props.selectedSalesReps}
        salesRepsData={props.salesRepsData}
        dataProvider={props.dataProvider}
      />
    </ContentCard>
  );
};

export const DashboardCards: React.FC<DashboardCardsProps> = (props) => {
  const metrics = useDashboardCardMetrics({
    dataProvider: props.dataProvider,
    period: props.chartPeriod,
    dashboardSales: (props as any).dashboardSales,
    dashboardReturns: (props as any).dashboardReturns,
    dashboardPurchasing: props.dashboardPurchasing,
    dashboardDispatched: props.dashboardDispatched,
    dashboardToShip: props.dashboardToShip,
    dashboardCreditNotes: props.dashboardCreditNotes,
    dashboardSalesMargins: props.dashboardSalesMargins,
  });

  

  return (
    <ContentCardsContainer>
      <DashboardExecutiveSummaryCard metrics={metrics} period={props.chartPeriod} />
      <DashboardChannelBreakdownCard metrics={metrics} period={props.chartPeriod} />
      <DashboardOperationsOverviewCard metrics={metrics} period={props.chartPeriod} />
      <DashboardHistoricalChartCard
        chartPeriod={props.chartPeriod}
        selectedBars={props.selectedBars}
        selectedSalesChannels={props.selectedSalesChannels}
        viewBySalesRep={props.viewBySalesRep}
        selectedSalesReps={props.selectedSalesReps}
        salesRepsData={props.salesRepsData}
        dataProvider={props.dataProvider}
      />
    </ContentCardsContainer>
  );
};